/**
 * Build-time configuration.
 *
 * Vite inlines `import.meta.env.VITE_*` at build time, so everything here ends
 * up readable in the bundle. Nothing secret may be added to this file.
 */

function normaliseBaseUrl(value: string): string {
  const trimmed = value.trim();
  return trimmed.endsWith('/') ? trimmed.replace(/\/+$/, '') : trimmed;
}

/**
 * Base URL of the API, without a trailing slash — e.g. `http://localhost:3001/api`.
 *
 * ## THERE IS NO DEFAULT ANY MORE, AND THAT IS THE POINT
 *
 * This used to fall back to `http://localhost:3001/api` so a fresh checkout ran
 * with no `.env` at all. That convenience had a cost nobody saw until a second
 * worktree existed: **3001 is the port the live API listens on**, the one whose
 * database holds 1,287 real addresses belonging to real cooperative banks. Any
 * checkout without a `VITE_API_URL` silently pointed its front-end at it.
 *
 * It was found the only way it could be — the development app on :5273 showing
 * *"Could not reach the Wend API"*, which was **CORS refusing the cross-wiring**
 * rather than anything being down. The live API allows `localhost:5173` and
 * `5174`; the development one allows `5273` and `5274`. Had those lists happened
 * to overlap, a development UI would have been reading and writing live data
 * with nothing on screen to say so.
 *
 * So an unset `VITE_API_URL` is now a startup failure rather than a guess. A
 * build that cannot say which API it talks to must not pick one, because the
 * wrong guess is indistinguishable from the right one until somebody sends.
 *
 * **Every worktree therefore needs its own `apps/web/.env`.** The development
 * one is `http://localhost:3101/api`; the live checkout is
 * `http://localhost:3001/api` and needs the file written before this change
 * reaches it, or its web app will refuse to boot.
 *
 * One more trap, met while fixing this: on Windows, `> file` from PowerShell
 * writes **UTF-16**, and dotenv reads that as nothing — so the variable stays
 * unset and the old default silently applied. The file must be UTF-8.
 */
function resolveApiBaseUrl(): string {
  const configured = import.meta.env.VITE_API_URL;
  if (configured === undefined || configured.trim() === '') {
    throw new Error(
      'VITE_API_URL is not set. Create apps/web/.env with VITE_API_URL=http://localhost:3101/api ' +
        '(development) or http://localhost:3001/api (live). It must be UTF-8 — PowerShell "> file" ' +
        'writes UTF-16, which dotenv reads as unset, which is how this was missed.',
    );
  }
  return normaliseBaseUrl(configured);
}

export const API_BASE_URL: string = resolveApiBaseUrl();
